
import { Ref, computed, defineComponent, ref } from 'vue';
import { attrValue, attrValues, additionalInfo } from '@/common/helpers/productAttributes';
import { DataType } from '@/catalogs/api/configuration/application/DataType';
import { ComponentName } from '@/catalogs/api/configuration/components/ComponentName';

import { setupComponent } from '@/catalogs/composables/setupComponent';

import InjectStyles from '@/common/components/InjectStyles.vue';
import { CatalogSortFieldEnum } from '@/common/services/swagger/index.defs';
import PaginationAmount from '@/common/components/PaginationAmount.vue';
import {
  CatalogsSortByCommand,
  CatalogsPageSizeCommand,
  CatalogsFilterByDateCommand,
} from '@/catalogs/api/runtime/CommandExecutor';

import DropdownPopper from '@/common/components/DropdownPopper.vue';
import useCatalogActions from '@/catalogs/composables/useCatalogActions';
import { Instance } from '@/catalogs/api/Instance';
import VRuntimeTemplate from 'vue3-runtime-template';
import { IMenuItem } from '@/catalogs/api/configuration/components/IExportMenuOptions';

export default defineComponent({
  name: ComponentName.searchTableToolbar,

  components: {
    InjectStyles,
    DropdownPopper,
    PaginationAmount,
    VRuntimeTemplate,
  },

  props: {
    instanceId: String,
  },

  setup(props) {
    const root = ref<HTMLElement>();

    const {
      componentName,
      isReady,
      isWebComponent,
      instance,
      store,
      routeData,
      narrowerThan,
      emit,
      t,
    } = setupComponent(root, props.instanceId, [DataType.Products]);

    const { onCatalogListMenuAction } = useCatalogActions(instance as Ref<Instance>, t);

    const sortByItems = CatalogSortFieldEnum;

    const createdItems = computed(() => [
      {
        val: '',
        text: t('CreatedAll'),
      },
      {
        val: 10,
        text: t('CreatedWithinDays', false, ['10']),
      },
      {
        val: 20,
        text: t('CreatedWithinDays', false, ['20']),
      },
      {
        val: 30,
        text: t('CreatedWithinDays', false, ['30']),
      },
    ]);

    const sortBy = computed({
      get: () => routeData.value?.catalogs.sortField as CatalogSortFieldEnum,
      set: (sortField: CatalogSortFieldEnum) =>
        instance.value?.execute(new CatalogsSortByCommand({ sortField })),
    });
    const totalCount = computed(() => store.value?.data.catalogs?.totalCount ?? 0);
    const pagination = computed(() => routeData.value?.catalogs.pagination);
    const pageSizeItems = computed(() => store.value?.options.components?.searchToolbar?.pageSizes);
    const pageSize = computed({
      get: () => pagination.value?.pageSize ?? 24,
      set: (pageSize: number) => instance.value?.execute(new CatalogsPageSizeCommand({ pageSize })),
    });

    const createdDate = computed({
      get: () => routeData.value?.catalogs.createdWithinLastDays ?? '',
      set: (days?: number | string | undefined) =>
        instance.value?.execute(new CatalogsFilterByDateCommand({ days })),
    });

    const catalogListMenu = computed(
      () => store.value?.options.components?.actions?.catalogListMenu,
    );

    const catalogListMenuItems = (): IMenuItem<void>[] =>
      (catalogListMenu.value?.items || []).filter((item: IMenuItem<void>) => {
        if (typeof item.visible === 'function') {
          return item.visible();
        }
        return item.visible;
      });

    const isDisabled = (item: IMenuItem<void>) =>
      typeof item.disabled === 'function' ? item.disabled() : item.disabled;

    const actionsEnabled = computed(
      () =>
        instance.value?.registeredComponents.has(ComponentName.catalogsActionModals) &&
        catalogListMenu.value?.visible,
    );

    return {
      root,
      store,
      sortBy,
      isReady,
      instance,
      pageSize,
      routeData,
      pagination,
      totalCount,
      createdDate,
      sortByItems,
      createdItems,
      componentName,
      pageSizeItems,
      isWebComponent,
      actionsEnabled,
      catalogListMenu,
      catalogListMenuItems,
      t,
      emit,
      attrValue,
      attrValues,
      isDisabled,
      narrowerThan,
      additionalInfo,
      onCatalogListMenuAction,
    };
  },
});
